import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Novo geslo — kupish.si" },
      { name: "robots", content: "noindex,nofollow" },
    ],
      links: [{ rel: "canonical", href: "https://kupish.si/reset-password" }],
  }),
  component: Page,
});

type Status = "checking" | "ready" | "invalid";

function Page() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("checking");
  const [accountEmail, setAccountEmail] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // 1) Najprej se naročimo na PASSWORD_RECOVERY event, ki ga Supabase sproži,
    //    ko je uporabnik prišel preko recovery povezave iz e-pošte.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === "PASSWORD_RECOVERY" && session?.user) {
        setAccountEmail(session.user.email ?? null);
        setStatus("ready");
      }
    });

    // 2) Fallback: če dogodek že manjka (npr. uporabnik osveži stran), preveri sejo.
    //    Veljaven recovery flow obdrži aktivno sejo z e-naslovom računa, ki ga ponastavljamo.
    (async () => {
      const hash = typeof window !== "undefined" ? window.location.hash : "";
      const isRecoveryHash = hash.includes("type=recovery");
      // Počakamo kratek trenutek, da Supabase obdela hash fragment
      await new Promise((r) => setTimeout(r, 300));
      const { data } = await supabase.auth.getUser();
      if (cancelled) return;
      if (data.user) {
        setAccountEmail(data.user.email ?? null);
        setStatus("ready");
      } else if (!isRecoveryHash) {
        setStatus("invalid");
      } else {
        // Hash je prisoten, a seja še ni vzpostavljena — počakaj na event
        setTimeout(() => {
          if (!cancelled && status === "checking") setStatus("invalid");
        }, 2500);
      }
    })();

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("Geslo mora imeti vsaj 8 znakov.");
      return;
    }
    if (password !== confirm) {
      setError("Gesli se ne ujemata.");
      return;
    }

    setLoading(true);
    // Dodatna varovalka: tik pred posodobitvijo gesla preveri, da je seja še vedno
    // tista, ki jo je prikazal recovery flow — prepreči zamenjavo gesla na napačnem računu,
    // če bi se v drugem zavihku medtem prijavil drug uporabnik.
    const { data: who } = await supabase.auth.getUser();
    if (!who.user || (accountEmail && who.user.email !== accountEmail)) {
      setLoading(false);
      setError(
        "Seja za ponastavitev gesla ni več veljavna. Ponovno zahtevajte povezavo za ponastavitev."
      );
      setStatus("invalid");
      return;
    }

    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      setError(error.message);
      return;
    }
    navigate({ to: "/dashboard", replace: true });
  }

  return (
    <section className="mx-auto max-w-md px-6 py-24">
      <div className="eyebrow">Novo geslo</div>
      <h1 className="font-serif text-5xl mt-3">Nastavite geslo</h1>

      {status === "checking" && (
        <p className="mt-6 text-sm text-muted-foreground">Preverjam povezavo za ponastavitev…</p>
      )}

      {status === "invalid" && (
        <div className="mt-10 border border-border p-6 text-sm space-y-3">
          <p>
            Povezava za ponastavitev gesla je neveljavna ali pa je že potekla.
          </p>
          <p>
            <Link to="/pozabljeno-geslo" className="underline underline-offset-4 hover:text-accent">
              Zahtevajte novo povezavo
            </Link>
          </p>
        </div>
      )}

      {status === "ready" && (
        <>
          <p className="mt-3 text-sm text-muted-foreground">
            Nastavljate novo geslo za račun{" "}
            <strong className="text-foreground">{accountEmail ?? "—"}</strong>.
          </p>
          <form className="mt-10 space-y-5" onSubmit={onSubmit}>
            <div>
              <label className="text-xs eyebrow">Novo geslo</label>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground"
              />
            </div>
            <div>
              <label className="text-xs eyebrow">Potrdite geslo</label>
              <input
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-2 w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-foreground"
              />
            </div>
            {error && <div className="text-sm text-destructive">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground py-3 text-sm hover:opacity-90 disabled:opacity-60"
            >
              {loading ? "Shranjujem…" : "Shrani novo geslo"}
            </button>
          </form>
        </>
      )}
    </section>
  );
}
